import { LinearCanonicalNetworkEditedRow } from '@blisspointmedia/bpm-types/dist/LinearAssignments';
import { SalesAndTrafficContact } from '@blisspointmedia/bpm-types/dist/SalesAndTrafficContacts';
import { OverviewFieldType, OverviewSection } from '@Components/EntityOverviewForm';
import {
  CheckIsValidFn,
  CustomModalEditWidgetHeader,
  SelectorOption
} from '@Components/ModalEditWidget/modal-edit-constants';
import { toOptions } from '@Components/ModalEditWidget/modal-edit-utils';
import { invalidEmailMessage, isInvalidOptionalEmail } from '@src/utils/email-utils';

export type ContactRow = SalesAndTrafficContact & { 'Short Code'?: string };
export interface RatesRow {
  id?: number;
  network: string; // joins to a canonical network's "Short Code"
  full_network: string;
  avail: string;
  status: string;
  program_name: string;
  rotation_name: string;
  market?: string;
  day_set: string;
  daypart_begin: string;
  daypart_end: string;
  cost_30s: string;
  company: string;
  valid?: string;
  dirty?: boolean;
}

export const WorkspaceTab = {
  OVERVIEW: 'overview',
  RATES: 'rates',
  CONTACTS: 'contacts'
} as const;
export type WorkspaceTabType = (typeof WorkspaceTab)[keyof typeof WorkspaceTab];

export const ViewMode = {
  FOCUS: 'focus',
  BULK: 'bulk'
} as const;
export type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode];

export const NetworkFilter = {
  ALL: 'all',
  LINEAR: 'linear',
  RADIO: 'radio'
} as const;
export type NetworkFilterType = (typeof NetworkFilter)[keyof typeof NetworkFilter];

const NETWORK_GROUP_FIELD = 'Network Group';
const SHORT_CODE_FIELD = 'Short Code';
const STATION_NAME_FIELD = 'Station Name';

export const shortCodeOf = (network: LinearCanonicalNetworkEditedRow): string =>
  network[SHORT_CODE_FIELD] ?? '';
export const stationNameOf = (network: LinearCanonicalNetworkEditedRow): string =>
  network[STATION_NAME_FIELD] ?? '';
export const networkGroupOf = (network: LinearCanonicalNetworkEditedRow): string =>
  network[NETWORK_GROUP_FIELD] ?? 'Ungrouped';
export const colorOf = (network: LinearCanonicalNetworkEditedRow): string | null => network.Color;

// Whether a persistable field has a non-empty value.
export const isTextValid = (value?: string): boolean => Boolean(value);

// ---- selector options ----

export const NETWORK_SELECTOR_OPTIONS: Record<string, SelectorOption[]> = {
  mediaTier: toOptions(['Lower Tier', 'Mid Tier', 'Upper Tier']),
  feed: toOptions(['Single', 'Dual', 'Triple', 'Quad']),
  language: [
    ...toOptions(['English', 'Spanish', 'Bilingual']),
    { label: 'Leave blank', value: '' }
  ],
  channel: toOptions(['Linear', 'Radio'])
};

export const RATE_STATUS_OPTIONS = toOptions([
  'buyable',
  'defunct',
  'inactive',
  'mirror',
  'paid programming',
  'secured',
  'general',
  'remnant',
  'upfront'
]);

export const CONTACT_SELECTOR_STATICS: Record<string, SelectorOption[]> = {
  avail: toOptions(['N', 'L']),
  general: toOptions(['G']),
  buyingType: toOptions(['Upfront', 'General', 'Regular']),
  department: toOptions(['Sales', 'Traffic']),
  primary: toOptions(['x'])
};

// ---- overview (single-network) read/edit form ----
// Shares its rendering with `@Components/EntityOverviewForm` — this module only supplies the
// field/section layout and the option lists specific to a linear canonical network.

// bpm-types types the SD/checkbox fields as string|null, but ModalEditWidget stores them as 0/1.
// These helpers keep the shared overview form writing the same values the backend expects.
export const isCheckboxChecked = (value: unknown): boolean => Boolean(Number(value));
export const checkboxValue = (checked: boolean): number => Number(checked);

// 'Network Group' is the one creatable field whose options are computed from the current
// network list at render time — NetworkOverview.tsx fills in `options` before rendering.
export const OVERVIEW_SECTIONS: OverviewSection[] = [
  {
    title: 'Identity',
    fields: [
      { field: 'Station Name', label: 'Station Name', type: OverviewFieldType.TEXT },
      { field: 'Short Code', label: 'Short Code', type: OverviewFieldType.TEXT, readOnly: true },
      {
        field: 'Network Group',
        label: 'Network Group',
        type: OverviewFieldType.SELECT,
        creatable: true,
        options: []
      },
      {
        field: 'channel',
        label: 'Channel',
        type: OverviewFieldType.SELECT,
        options: NETWORK_SELECTOR_OPTIONS.channel
      },
      {
        field: 'Media Tier',
        label: 'Media Tier',
        type: OverviewFieldType.SELECT,
        options: NETWORK_SELECTOR_OPTIONS.mediaTier
      },
      { field: 'Genre', label: 'Genre', type: OverviewFieldType.TEXT }
    ]
  },
  {
    title: 'Broadcast',
    fields: [
      {
        field: 'Feed',
        label: 'Feed',
        type: OverviewFieldType.SELECT,
        options: NETWORK_SELECTOR_OPTIONS.feed
      },
      {
        field: 'Language',
        label: 'Language',
        type: OverviewFieldType.SELECT,
        options: NETWORK_SELECTOR_OPTIONS.language
      },
      {
        field: 'SD Only',
        label: 'SD Only',
        type: OverviewFieldType.CHECKBOX,
        toChecked: isCheckboxChecked,
        fromChecked: checkboxValue
      },
      {
        field: 'SD Companion',
        label: 'SD Companion',
        type: OverviewFieldType.CHECKBOX,
        toChecked: isCheckboxChecked,
        fromChecked: checkboxValue
      },
      {
        field: 'S&P Clearance',
        label: 'S&P Clearance',
        type: OverviewFieldType.CHECKBOX,
        toChecked: isCheckboxChecked,
        fromChecked: checkboxValue
      }
    ]
  },
  {
    title: 'Endpoints & IDs',
    fields: [
      { field: 'Traffic Endpoint', label: 'Traffic Endpoint', type: OverviewFieldType.TEXT },
      { field: 'XR Destination', label: 'XR Destination', type: OverviewFieldType.TEXT },
      { field: 'destination_id', label: 'Destination ID', type: OverviewFieldType.TEXT },
      { field: 'HH UEs', label: 'HH UEs', type: OverviewFieldType.TEXT },
      {
        field: 'UEs Likely to Mislead',
        label: 'UEs Likely to Mislead',
        type: OverviewFieldType.TEXT
      }
    ]
  },
  {
    title: 'Presentation',
    fields: [
      { field: 'Color', label: 'Color', type: OverviewFieldType.COLOR },
      { field: 'Network Group Color', label: 'Network Group Color', type: OverviewFieldType.COLOR }
    ]
  }
];

// ---- header definitions ----

// Network metadata columns, grouped into modal rows (mirrors the bpm canonical-networks layout).
export const NETWORK_HEADERS: CustomModalEditWidgetHeader[] = [
  { label: 'Network Group', field: 'Network Group', modalRow: 0, modalWidth: 200, width: 180 },
  { label: 'Short Code', field: 'Short Code', modalRow: 0, modalWidth: 200, width: 120 },
  { label: 'Station Name', field: 'Station Name', modalRow: 1, modalWidth: 200, flex: 1 },
  {
    label: 'Channel',
    field: 'channel',
    type: 'select',
    options: 'channel',
    modalRow: 1,
    modalWidth: 200,
    width: 120
  },
  {
    label: 'Feed',
    field: 'Feed',
    type: 'select',
    options: 'feed',
    modalRow: 2,
    modalWidth: 200,
    width: 120
  },
  {
    label: 'Language',
    field: 'Language',
    type: 'select',
    options: 'language',
    modalRow: 2,
    modalWidth: 200,
    width: 120
  },
  {
    label: 'SD Only',
    field: 'SD Only',
    type: 'checkbox',
    modalRow: 3,
    modalWidth: 200,
    width: 100
  },
  {
    label: 'SD Companion',
    field: 'SD Companion',
    type: 'checkbox',
    modalRow: 3,
    modalWidth: 200,
    width: 120
  },
  {
    label: 'S&P Clearance',
    field: 'S&P Clearance',
    type: 'checkbox',
    modalRow: 3,
    modalWidth: 200,
    width: 120
  },
  {
    label: 'Media Tier',
    field: 'Media Tier',
    type: 'select',
    options: 'mediaTier',
    modalRow: 4,
    modalWidth: 200,
    width: 120
  },
  { label: 'Genre', field: 'Genre', modalRow: 4, modalWidth: 200, width: 140 },
  {
    label: 'Traffic Endpoint',
    field: 'Traffic Endpoint',
    modalRow: 5,
    modalWidth: 200,
    width: 160
  },
  { label: 'XR Destination', field: 'XR Destination', modalRow: 5, modalWidth: 200, width: 160 },
  { label: 'HH UEs', field: 'HH UEs', modalRow: 6, modalWidth: 200, width: 120 },
  {
    label: 'UEs Likely to Mislead',
    field: 'UEs Likely to Mislead',
    modalRow: 6,
    modalWidth: 200,
    width: 160
  },
  { label: 'Color', field: 'Color', modalRow: 7, modalWidth: 200, width: 120 },
  {
    label: 'Network Group Color',
    field: 'Network Group Color',
    modalRow: 7,
    modalWidth: 200,
    width: 160
  },
  { label: 'Destination ID', field: 'destination_id', modalRow: 8, modalWidth: 200, width: 120 }
];

/**
 * Rates columns. In focus mode the network is inherited from the selected network (read-only);
 * in bulk mode it becomes a dropdown sourced from canonical networks so a network name can never
 * be free-typed. full_network is always derived, so it is read-only in both modes.
 */
export const buildRatesHeaders = ({ bulk }: { bulk: boolean }): CustomModalEditWidgetHeader[] => {
  // Network & full network only appear in bulk (cross-network) mode. In focus mode they're implied
  // by the selected network and inherited via defaultNewRow, so showing them on every row is noise.
  const networkColumns: CustomModalEditWidgetHeader[] = bulk
    ? [
        {
          label: 'Network',
          field: 'network',
          type: 'select',
          options: 'networkCode',
          searchable: true,
          creatable: false,
          modalRow: 0,
          modalWidth: 165,
          width: 140
        },
        {
          label: 'Full Network',
          field: 'full_network',
          readOnly: true,
          modalRow: 0,
          modalWidth: 300,
          flex: 1
        }
      ]
    : [];

  return [
    ...networkColumns,
    {
      label: 'Avail',
      field: 'avail',
      type: 'select',
      options: 'availOptions',
      modalRow: 0,
      modalWidth: 80,
      width: 80
    },
    {
      label: 'Status',
      field: 'status',
      type: 'select',
      options: 'statusOptions',
      modalRow: 0,
      modalWidth: 150,
      width: 120
    },
    { label: 'Program Name', field: 'program_name', modalRow: 1, modalWidth: 200, flex: 1 },
    { label: 'Rotation Name', field: 'rotation_name', modalRow: 1, modalWidth: 200, width: 160 },
    {
      label: 'Market',
      field: 'market',
      type: 'select',
      options: 'marketOptions',
      isClearable: true,
      modalRow: 1,
      modalWidth: 200,
      width: 120,
      searchable: true
    },
    { label: 'Days', field: 'day_set', modalRow: 2, modalWidth: 100, width: 100 },
    {
      label: 'Start',
      field: 'daypart_begin',
      type: 'time',
      modalRow: 2,
      modalWidth: 160,
      width: 90
    },
    { label: 'End', field: 'daypart_end', type: 'time', modalRow: 2, modalWidth: 160, width: 90 },
    {
      label: 'Cost 30s',
      field: 'cost_30s',
      type: 'currency',
      modalRow: 2,
      modalWidth: 100,
      width: 100
    },
    { label: 'Company', field: 'company', modalRow: 2, modalWidth: 180, width: 120 }
  ];
};

// Extra options that only exist alongside marketOptions.
export const AVAIL_OPTIONS = toOptions(['N', 'L']);

/**
 * Contacts columns. In focus mode the station is inherited (read-only) and Short Code is derived;
 * in bulk mode station becomes a dropdown of canonical station names.
 */
export const buildContactsHeaders = ({
  bulk
}: {
  bulk: boolean;
}): CustomModalEditWidgetHeader[] => {
  // Station & short code only appear in bulk mode. In focus mode they're implied by the selected
  // network and inherited via defaultNewRow.
  const stationColumns: CustomModalEditWidgetHeader[] = bulk
    ? [
        {
          label: 'Station',
          field: 'Canonical Station',
          type: 'select',
          options: 'station',
          searchable: true,
          creatable: false,
          modalRow: 0,
          modalFlex: 2,
          width: 320
        },
        {
          label: 'Short Code',
          field: 'Short Code',
          readOnly: true,
          modalRow: 0,
          modalFlex: 1,
          width: 120
        }
      ]
    : [];

  return [
    ...stationColumns,
    {
      label: 'Avail',
      field: 'Avail',
      type: 'select',
      options: 'avail',
      modalRow: 0,
      modalWidth: 100,
      width: 90
    },
    {
      label: 'Primary',
      field: 'Primary',
      type: 'select',
      options: 'primary',
      isClearable: true,
      modalRow: 0,
      modalWidth: 100,
      width: 100
    },
    { label: 'First Name', field: 'First Name', modalRow: 1, modalFlex: 1, flex: 1 },
    { label: 'Last Name', field: 'Last Name', modalRow: 1, modalFlex: 1, flex: 1 },
    { label: 'Phone', field: 'Phone', modalRow: 2, modalFlex: 2, width: 160 },
    {
      label: 'Email',
      field: 'Email',
      isInvalid: isInvalidOptionalEmail,
      modalRow: 2,
      modalFlex: 3,
      width: 260
    },
    {
      label: 'General',
      field: 'General',
      type: 'select',
      options: 'general',
      isClearable: true,
      modalRow: 3,
      modalWidth: 100,
      width: 100
    },
    {
      label: 'Department',
      field: 'Department',
      type: 'select',
      options: 'department',
      modalRow: 3,
      modalFlex: 1,
      width: 120
    },
    {
      label: 'Buying Type',
      field: 'buying_type',
      type: 'select',
      options: 'buyingType',
      modalRow: 3,
      modalWidth: 160,
      width: 140
    },
    { label: 'Notes', field: 'Notes', modalRow: 4, modalFlex: 1, flex: 1 }
  ];
};

// Fields required for a canonical-network row to be persistable. Unlike bpm we do NOT require
// `lastmodified` (that dropped brand-new rows on the floor); identity + core buy fields is enough.
export const isNetworkPersistable = (network: LinearCanonicalNetworkEditedRow): boolean =>
  isTextValid(network[SHORT_CODE_FIELD]) &&
  isTextValid(network[STATION_NAME_FIELD]) &&
  Boolean(network['Media Tier']) &&
  Boolean(network.Feed);

// The bulk-table editor (ModalEditWidget/ModalEditor) now writes these checkbox columns as real
// booleans. Re-derive the 0/1 the backend expects before persisting, matching what the NetworkOverview
// editor already writes via checkboxValue() — isCheckboxChecked() reads both representations.
export const normalizeNetworkCheckboxes = (
  network: LinearCanonicalNetworkEditedRow
): LinearCanonicalNetworkEditedRow => {
  const checkboxOverrides: Record<string, number> = {
    'SD Only': checkboxValue(isCheckboxChecked(network['SD Only'])),
    'SD Companion': checkboxValue(isCheckboxChecked(network['SD Companion'])),
    'S&P Clearance': checkboxValue(isCheckboxChecked(network['S&P Clearance']))
  };
  return { ...network, ...checkboxOverrides };
};

// A blank contact email stays acceptable — plenty of contact records only carry a phone number.
// Anything actually typed, though, has to be a well-formed address.
export const checkContactIsValid: CheckIsValidFn<ContactRow> = (contact, handleInvalidMessage) => {
  const email = contact.Email ?? '';
  if (isInvalidOptionalEmail(email)) {
    handleInvalidMessage?.(invalidEmailMessage(email));
    return false;
  }
  return true;
};

// ---- hoisted header arrays ----
// Built once at module load rather than allocating a fresh array on every render.
export const RATES_HEADERS_FOCUS = buildRatesHeaders({ bulk: false });
export const RATES_HEADERS_BULK = buildRatesHeaders({ bulk: true });
export const CONTACTS_HEADERS_FOCUS = buildContactsHeaders({ bulk: false });
export const CONTACTS_HEADERS_BULK = buildContactsHeaders({ bulk: true });

// ---- deep search across a network and its children ----

export interface NetworkSearchMatch {
  matched: boolean;
  hitTab: WorkspaceTabType | null; // which child tab surfaced the match, if any
}

// Lower-cased, concatenated search text for a network, split by which tab it belongs to. Built once
// per network (in the page's derived data) so each keystroke is a cheap substring check, not a full
// re-scan of every rate and contact.
export interface NetworkSearchBlobs {
  name: string;
  rates: string;
  contacts: string;
}

export const buildSearchBlobs = (
  network: LinearCanonicalNetworkEditedRow,
  rates: RatesRow[],
  contacts: SalesAndTrafficContact[]
): NetworkSearchBlobs => ({
  name: [stationNameOf(network), shortCodeOf(network), networkGroupOf(network)]
    .join(' ')
    .toLowerCase(),
  rates: rates
    .map(rate => `${rate.program_name ?? ''} ${rate.rotation_name ?? ''}`)
    .join(' ')
    .toLowerCase(),
  contacts: contacts
    .map(
      contact =>
        `${contact['First Name'] ?? ''} ${contact['Last Name'] ?? ''} ${contact.Email ?? ''} ${contact.Phone ?? ''}`
    )
    .join(' ')
    .toLowerCase()
});

export const matchSearchBlobs = (
  blobs: NetworkSearchBlobs,
  rawQuery: string
): NetworkSearchMatch => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return { matched: true, hitTab: null };
  if (blobs.name.includes(query)) return { matched: true, hitTab: null };
  if (blobs.rates.includes(query)) return { matched: true, hitTab: WorkspaceTab.RATES };
  if (blobs.contacts.includes(query)) return { matched: true, hitTab: WorkspaceTab.CONTACTS };
  return { matched: false, hitTab: null };
};
